import type React from "react";
import { forwardRef } from "react";

interface TextEditorProps {
  placeholder?: string;
  disabled?: boolean;
  onFocus: () => void;
  onInput: (e: React.FormEvent<HTMLDivElement>) => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => void;
  onPaste?: (e: React.ClipboardEvent<HTMLDivElement>) => void;
  className?: string;
  maxHeight?: number; // 最大高さ（px）
}

export const TextEditor = forwardRef<HTMLDivElement, TextEditorProps>(
  (
    {
      placeholder,
      disabled,
      onFocus,
      onInput,
      onKeyDown,
      onPaste,
      className = "",
      maxHeight,
    },
    ref,
  ) => {
    const handleInput = (e: React.FormEvent<HTMLDivElement>) => {
      const target = e.target as HTMLDivElement;
      const content = target.textContent || "";

      // If content is empty, ensure the element is truly empty for placeholder to show
      if (content.trim() === "") {
        target.innerHTML = "";
      }

      onInput(e);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      // Handle delete/backspace when content becomes empty
      if (e.key === "Backspace" || e.key === "Delete") {
        const target = e.target as HTMLDivElement;
        setTimeout(() => {
          const content = target.textContent || "";
          if (content.trim() === "") {
            target.innerHTML = "";
          }
        }, 0);
      }

      onKeyDown(e);
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
      if (onPaste) {
        onPaste(e);
      }
    };

    // コンテキストに基づくデフォルト最大高さ
    const defaultMaxHeight = 360;
    const effectiveMaxHeight = maxHeight || defaultMaxHeight;

    // 最大高さが設定されている場合のスタイル
    const maxHeightStyles = effectiveMaxHeight
      ? {
          maxHeight: `${effectiveMaxHeight}px`,
          overflowY: "auto" as const,
          scrollbarWidth: "thin" as const,
        }
      : {};

    return (
      <div
        ref={ref}
        contentEditable={!disabled}
        className={`relative min-h-6 w-full max-w-full whitespace-pre-wrap break-words leading-6 outline-none [&:empty]:before:pointer-events-none [&:empty]:before:leading-6 [&:empty]:before:text-custom-placeholder [&:empty]:before:content-[attr(data-placeholder)] ${className}`}
        data-placeholder={placeholder}
        onFocus={onFocus}
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        style={{
          wordBreak: "break-word",
          overflowWrap: "break-word",
          ...maxHeightStyles,
        }}
      />
    );
  },
);

TextEditor.displayName = "TextEditor";
